/* One fetch wrapper for the whole console: same-origin, JSON in and out, and
   the plane's one error envelope turned into an ApiError the UI can render. */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fix?: string;
  readonly requestId?: string;

  constructor(status: number, code: string, message: string, fix?: string, requestId?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.fix = fix;
    this.requestId = requestId;
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();

/** Called when any call (other than auth itself) answers 401: the session is gone. */
export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, headers = {} } = options;
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: { accept: 'application/json', ...(body !== undefined ? { 'content-type': 'application/json' } : {}), ...headers },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new ApiError(0, 'network', 'The console could not reach the offstage API.', 'Check your connection. The page retries on its own.');
  }
  const requestId = res.headers.get('x-request-id') ?? undefined;
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const envelope = (data as { error?: { code?: string; message?: string; fix?: string; requestId?: string } } | null)?.error;
    const error = new ApiError(
      res.status,
      envelope?.code ?? `http-${res.status}`,
      envelope?.message ?? `The API answered ${res.status} ${res.statusText}.`,
      envelope?.fix,
      envelope?.requestId ?? requestId,
    );
    if (res.status === 401 && !path.startsWith('/v1/auth/')) unauthorizedListeners.forEach((l) => l());
    throw error;
  }
  return data as T;
}

/** Plain text (Markdown docs). */
export async function apiText(path: string, signal?: AbortSignal): Promise<string> {
  let res: Response;
  try {
    res = await fetch(path, { credentials: 'same-origin', signal });
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new ApiError(0, 'network', 'The console could not reach the offstage API.', 'Check your connection and reload.');
  }
  if (!res.ok) throw new ApiError(res.status, `http-${res.status}`, `${path} answered ${res.status}.`);
  return res.text();
}

export function asApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  return new ApiError(0, 'unexpected', error instanceof Error ? error.message : String(error));
}
